import React from 'react';
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { MdPushPin } from 'react-icons/md';

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 2, 1, 2), // 16px left padding for alignment
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  letterSpacing: '0.5px',
}));

const PinButton = styled(IconButton)(({ theme }) => ({
  padding: 4,
  opacity: 0,
  transition: 'opacity 0.2s',
  '&:hover': {
    backgroundColor: 'rgba(77, 166, 133, 0.08)',
    color: theme.palette.secondaryNew.main,
  },
  '.MuiListItem-root:hover &': {
    opacity: 1,
  },
}));

/**
 * Current Page Indicator
 *
 * Shows the currently active page when it's not in the pinned section.
 * Provides context for "where am I?" and quick pin access.
 *
 * Only visible when:
 * - User is on a valid route
 * - Route is not a hero route (Recipe Designer/Results)
 * - Route is not already pinned
 */
export default function CurrentPageIndicator({
  route,
  onNavigate,
  onPin,
}) {
  if (!route) {
    return null;
  }

  const handleNavigate = (e) => {
    if (onNavigate) {
      onNavigate(route.path, route, e);
    }
  };

  const handlePin = (e) => {
    e.stopPropagation();
    if (onPin) {
      onPin(route.id);
    }
  };

  return (
    <Box>
      <SectionHeader>
        <SectionTitle>CURRENTLY VIEWING</SectionTitle>
      </SectionHeader>

      <ListItem
        disablePadding
        secondaryAction={
          <Tooltip title="Pin page" placement="right">
            <PinButton size="small" onClick={handlePin}>
              <MdPushPin size={16} />
            </PinButton>
          </Tooltip>
        }
        sx={{
          mb: 1,
        }}
      >
        <ListItemButton
          selected
          onClick={handleNavigate}
          sx={{
            pl: 2, // Consistent left padding
            pr: 2,
            borderLeft: '3px solid transparent',
            alignItems: 'center',
            backgroundColor: 'rgba(77, 166, 133, 0.04)',
            borderColor: 'secondaryNew.main',
            '&:hover': {
              backgroundColor: 'rgba(77, 166, 133, 0.08)',
            },
          }}
        >
          <ListItemText
            primary={
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: 'secondaryNew.main',
                }}
              >
                {route.label}
              </Typography>
            }
          />
        </ListItemButton>
      </ListItem>
    </Box>
  );
}
